import React from 'react';

import { useAppDispatch, useAppSelector } from '@/store';
import { enqueueGeneration, isCreativesSurfaceJob, selectCreativeJobsMap, sortJobsNewestFirst } from '@/store/slices/creativeJobs';

/**
 * The creatives state machine: the brand's live in-website creatives, and the run in flight
 * against them.
 *
 * A creative edit is a pipeline run, and it is PREVIEW-first, so the flow has two settling
 * points rather than one:
 *
 *   idle ─generate()─► uploading? ─► running ─► preview (pending artwork, live site UNCHANGED)
 *                                         │                     │
 *                                         └─► failed            └─apply()─► idle (family refetched)
 *
 * Between the run settling and `apply()`, the live site still serves the CURRENT creative; the
 * new artwork sits in `preview` until the owner commits it. "Use my own artwork" skips the run
 * entirely: it uploads the file and writes the URL straight in (`placeOwn`).
 *
 * One run per brand at a time (casino-creation refuses a second), and everything is keyed by
 * `tenantId` so switching brands drops the previous brand's family and any in-flight poll. A
 * queued run is written to `localStorage` so it survives a reload, mirroring the logo flow.
 * See `./creative-api`.
 */
import {
  applyCreativePreview,
  creativeApiConfigured,
  creativesServiceConfigured,
  fetchCreativeFamilyFromService,
  fetchCreativeRunStatus,
  fetchLiveCreativeFamily,
  fetchLogoCreativeRunStatus,
  markBannerSeen,
  publishBanner,
  deleteBannerVersion,
  restoreBannerVersion,
  queueCreativeRun,
  replaceLogoCreative,
  uploadArtwork,
  type CreativeFamily,
  type CreativeRunMode,
  type PublishResult,
} from './creative-api';
import { loadBannerFamily } from './banner-family-load';
import {
  familyDoneWatchJobs,
  isDoneRun,
  isFormatsRunComplete,
  progressKeyOf,
  seenFamilyJobs,
} from './family-refetch-jobs';
import { withVersionArchived } from './family-archive';
import { formatTargetsStillIncomplete, type FormatTarget } from './format-completeness';

const POLL_INTERVAL_MS = 4000;
const POLL_TIMEOUT_MS = 10 * 60 * 1000;

// After a formats job reaches its total, re-read the family until the version actually holds every slot.
// Bounded so a genuinely-stuck backend can't poll forever; sized to comfortably cover the read-after-write
// lag (a few seconds) and stay under the editor's finishing-bridge safety net.
const FORMATS_CONFIRM_ATTEMPTS = 8;
const FORMATS_CONFIRM_DELAY_MS = 1500;

/** A run the owner has started against a placement but that has not settled yet. */
export interface PendingCreativeRun {
  placement: string;
  mode: CreativeRunMode;
  phase: 'uploading' | 'running';
}

/** A settled preview awaiting the owner's Apply — the pending artwork, live site untouched. */
export interface CreativePreview {
  placement: string;
  urls: string[];
}

export type CreativeFailureReason = 'queue' | 'run' | 'lost' | 'refresh';

export interface CreativeFailure {
  placement: string;
  reason: CreativeFailureReason;
  /** Diagnostics only — never shown to the owner. */
  detail?: string;
}

/** What the owner asked a generation run to do. Exactly one of `prompt` / `uploadDataUri` /
 *  `bannerSetId` is meaningful per `mode`. */
export interface GenerateInput {
  mode: CreativeRunMode;
  /** A natural-language brief for a `generate` run. */
  prompt?: string;
  /** An owner-uploaded image (data URI) to seed a `generate` run with. */
  uploadDataUri?: string;
  /** The banner-set id for a `restyle` run. */
  bannerSetId?: string;
  forceRegen?: boolean;
  /** Target ONE existing banner (edit it) rather than adding a new creative to the placement. */
  bannerId?: string;
  /** The current artwork of the targeted banner, so the backend can image-edit from it. */
  baseImageUrl?: string;
  /** Owner-attached images (data URIs) to guide a regeneration — reproduced as closely as
   *  possible. Uploaded to CDN before the run, then passed as reference urls. */
  referenceDataUris?: string[];
  /** The exact version this regeneration branched from (lineage). */
  parentVersionId?: string;
  /** Which of the brand's mascots the run draws (`'none'` or an id); omitted = auto. */
  mascotId?: string;
  /** Draw from the banner's casino-creation definition (a fresh draw: no `baseImageUrl`). */
  useDefinition?: boolean;
  /** Which of the brand's art styles the run draws in; omitted = the brand's active one. */
  styleId?: string;
}

/** What {@link CreativesState.generate} resolves to. `needed` is the credit shortfall from a 402, so
 *  the caller can open Buy-credits instead of showing a generic error. */
export interface GenerateResult {
  ok: boolean;
  needed?: number;
  /** The service's refusal code when it refused to start the run (e.g. `banner_definition_unavailable`). */
  refusal?: string;
}

export interface CreativesState {
  family: CreativeFamily | undefined;
  /** True until a read of the CURRENT brand's family has applied an answer (also while the brand resolves). */
  loading: boolean;
  /** The latest read failed before any answer for this brand applied: an error with a retry, never "empty". */
  loadFailed: boolean;
  /** Read the family again (the error state's retry, and a banner whose live art changed). */
  reload: () => void;
  /** The run currently in flight, or undefined. Only one per brand at a time. */
  pending: PendingCreativeRun | undefined;
  /** A settled preview awaiting Apply, or undefined. */
  preview: CreativePreview | undefined;
  /** A commit (`apply`) or direct write (`placeOwn`) is in flight. */
  committing: boolean;
  failure: CreativeFailure | undefined;
  /**
   * Start a generation/restyle. Resolves once the pipeline has SETTLED and the preview is
   * ready (or false on failure) — the owner then reviews it and calls {@link apply}. Never
   * rejects.
   */
  generate: (placement: string, input: GenerateInput) => Promise<GenerateResult>;
  /** Commit a placement's pending preview to the live site, then refetch the family. */
  apply: (placement: string) => Promise<boolean>;
  /** Drop a pending preview without committing it. */
  discard: () => void;
  /** Replace a logo-family creative (identified by its slot / `banner_id`) with the owner's own
   *  artwork, through the launched-brand logo pipeline (upload, queue, poll, refetch). */
  placeOwn: (slot: string, imageDataUri: string) => Promise<boolean>;
  /** Publish a version to the brand's LIVE site (async job, tracked by the tray). `ok:false` with
   *  `needed`/`balance` is the insufficient-credits answer so the caller can open Buy-credits. */
  publish: (rootBannerId: string, versionId: string | undefined) => Promise<PublishResult>;
  /** Archive a regenerated version so it drops out of the family into "Archived"; resolves to whether
   *  it was archived. The initial / a published version can't be archived. */
  deleteVersion: (versionId: string) => Promise<boolean>;
  /** Recover an archived version back into the family; resolves to whether it was restored. */
  restoreVersion: (versionId: string) => Promise<boolean>;
  /** Mark a banner family SEEN (clears the card's "new" cue), optimistically + on the server. */
  markSeen: (rootBannerId: string) => void;
}

/* ── surviving a reload (mirrors logo-replacement) ──────────────────────────── */

const RUN_STORAGE_PREFIX = 'ows.creative-run.';
const runKey = (tenantId: number): string => `${RUN_STORAGE_PREFIX}${tenantId}`;

export interface StoredCreativeRun {
  pipelineId: string;
  placement: string;
  mode: CreativeRunMode;
  startedAt: number;
}

/** Parse a stored run, rejecting anything not a complete, safe record. `pipelineId` is
 *  interpolated into a proxied request path, so it is shape-checked here. */
export const parseStoredCreativeRun = (raw: string | null): StoredCreativeRun | undefined => {
  if (!raw) return undefined;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return undefined;
  }
  if (typeof parsed !== 'object' || parsed === null) return undefined;
  const { pipelineId, placement, mode, startedAt } = parsed as Partial<StoredCreativeRun>;
  if (typeof pipelineId !== 'string' || !/^[A-Za-z0-9_-]{1,128}$/.test(pipelineId)) return undefined;
  if (typeof placement !== 'string' || placement === '') return undefined;
  if (mode !== 'generate' && mode !== 'restyle') return undefined;
  if (typeof startedAt !== 'number' || !Number.isFinite(startedAt)) return undefined;
  return { pipelineId, placement, mode, startedAt };
};

/** Whether a stored run is still worth resuming (bounded from when it STARTED). */
export const isCreativeRunResumable = (run: StoredCreativeRun | undefined, now: number): boolean =>
  run !== undefined && now < run.startedAt + POLL_TIMEOUT_MS;

const readStoredRun = (tenantId: number): StoredCreativeRun | undefined => {
  try {
    return parseStoredCreativeRun(window.localStorage.getItem(runKey(tenantId)));
  } catch {
    return undefined;
  }
};
const writeStoredRun = (tenantId: number, run: StoredCreativeRun): void => {
  try {
    window.localStorage.setItem(runKey(tenantId), JSON.stringify(run));
  } catch {
    /* storage unavailable — the run still works, it just won't survive a reload */
  }
};
const clearStoredRun = (tenantId: number): void => {
  try {
    window.localStorage.removeItem(runKey(tenantId));
  } catch {
    /* nothing to remove */
  }
};

const sleep = (ms: number, signal: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    let timer = 0;
    const onAbort = (): void => {
      window.clearTimeout(timer);
      reject(new Error('aborted'));
    };
    timer = window.setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal.addEventListener('abort', onAbort, { once: true });
  });

type RunOutcome = { outcome: 'settled' | 'failed' | 'timeout'; previewUrls?: string[] };

/** Poll one run until it is over. A failed status REQUEST is skipped (ordinary over a
 *  minutes-long poll); the absolute deadline is what bounds the loop. */
const waitForRun = async (
  pipelineId: string,
  tenantId: number,
  deadline: number,
  signal: AbortSignal,
): Promise<RunOutcome> => {
  while (Date.now() < deadline) {
    await sleep(POLL_INTERVAL_MS, signal);
    let status;
    try {
      status = await fetchCreativeRunStatus(pipelineId, tenantId, signal);
    } catch (error) {
      if (signal.aborted) throw error;
      continue;
    }
    if (status.failed) return { outcome: 'failed' };
    if (status.settled) return { outcome: 'settled', previewUrls: status.previewUrls };
  }
  return { outcome: 'timeout' };
};

/**
 * The active brand's creatives and the operations on them. `tenantId` keys everything so one
 * casino's run can never be attributed to another.
 */
export const useCreatives = (tenantId: number | undefined, brandName: string, domain?: string): CreativesState => {
  const dispatch = useAppDispatch();
  const [family, setFamily] = React.useState<CreativeFamily | undefined>(undefined);
  // Where the read of the CURRENT brand's family stands: 'loading' until a read for it has applied an answer
  // (so the first frame, and a brand still resolving, show the skeleton), 'failed' when the latest read failed
  // before any answer applied, 'ready' once one has.
  const [readStatus, setReadStatus] = React.useState<'loading' | 'ready' | 'failed'>('loading');
  const [pending, setPending] = React.useState<PendingCreativeRun | undefined>(undefined);
  const [preview, setPreview] = React.useState<CreativePreview | undefined>(undefined);
  const [committing, setCommitting] = React.useState(false);
  const [failure, setFailure] = React.useState<CreativeFailure | undefined>(undefined);

  // Durable async jobs (service path): the tray owns progress; here we only watch for a job
  // that FINISHED for this tenant, to refetch the family so the new creative appears in place.
  const byId = useAppSelector(selectCreativeJobsMap);
  // Whether the jobs list has loaded (on a direct load of the page it lands after the page opened).
  const jobsListed = useAppSelector((state) => state.creativeJobs.loaded);
  // Art Style sample draws share the jobs list but never touch the creatives family, so they are left out.
  const jobs = React.useMemo(() => sortJobsNewestFirst(byId).filter(isCreativesSurfaceJob), [byId]);
  // The jobs whose end reads the family again: these, plus the brand's site jobs (see `familyDoneWatchJobs`).
  const doneWatchJobs = React.useMemo(() => familyDoneWatchJobs(byId), [byId]);
  // The latest jobs, read by the per-brand seeding below without re-seeding on every job update.
  const jobsRef = React.useRef(doneWatchJobs);
  jobsRef.current = doneWatchJobs;

  const abortRef = React.useRef<AbortController | undefined>(undefined);
  const pendingRef = React.useRef<PendingCreativeRun | undefined>(undefined);
  pendingRef.current = pending;
  // Monotonic guard shared by every family REFETCH (done-watcher + mid-run progress). Because the two
  // can race and responses can resolve out of order, only the LATEST-dispatched refetch is applied, so
  // the grid never flickers backwards to a smaller (earlier) format set.
  const familyReqRef = React.useRef(0);
  /** Apply a family read's answer while it is still the latest read: it becomes the family and the brand's family
   *  has loaded. A read superseded by a later one is dropped, so it never ends the loading either. */
  const applyRead = React.useCallback(
    (reqId: number, controller: AbortController, next: CreativeFamily | undefined): void => {
      if (controller.signal.aborted || reqId !== familyReqRef.current) return;
      setFamily(next);
      setReadStatus('ready');
    },
    [],
  );
  /** A family read failed. While it is the latest read and no answer has applied yet, that is the page's error
   *  state; once a family is shown, a failed refetch leaves it in place. A brand switch's abort is no failure. */
  const failRead = React.useCallback((reqId: number, controller: AbortController): void => {
    if (controller.signal.aborted || reqId !== familyReqRef.current) return;
    setReadStatus((status) => (status === 'ready' ? status : 'failed'));
  }, []);
  /** Read the brand's family under the shared request guard. The spark-creatives service is the ONLY source of
   *  banner content (real site banners it reads server-side + generated creatives); see `loadBannerFamily` for
   *  why an unreachable service is a failed read and never falls back to the brand's logo family. */
  const readFamily = React.useCallback(
    (tenant: number, controller: AbortController): void => {
      const reqId = ++familyReqRef.current;
      void loadBannerFamily({
        serviceConfigured: creativesServiceConfigured(),
        fetchFromService: () => fetchCreativeFamilyFromService(tenant, domain, controller.signal),
      }).then((result) => {
        if (result.ok) applyRead(reqId, controller, result.family);
        else failRead(reqId, controller);
      });
    },
    [domain, applyRead, failRead],
  );
  // Synchronous re-entry latch for the ASYNC service path (which never sets `pendingRef`): a
  // generation charges credits + enqueues a job, and the derived `generating` state only turns on
  // after that POST returns, so without this a fast second click would double-charge.
  const submittingRef = React.useRef(false);

  /** Follow one queued run to its end and stage its preview (never refetches the live family —
   *  a preview run does not change what the site serves). An abort leaves the stored run for
   *  the next mount to resume. */
  const follow = React.useCallback(
    async (run: StoredCreativeRun, tenant: number, controller: AbortController): Promise<void> => {
      try {
        const { outcome, previewUrls } = await waitForRun(
          run.pipelineId,
          tenant,
          run.startedAt + POLL_TIMEOUT_MS,
          controller.signal,
        );
        if (controller.signal.aborted) return;
        clearStoredRun(tenant);
        setPending(undefined);
        if (outcome === 'failed') {
          setFailure({ placement: run.placement, reason: 'run' });
        } else if (outcome === 'timeout') {
          setFailure({ placement: run.placement, reason: 'lost' });
        } else {
          setPreview({ placement: run.placement, urls: previewUrls ?? [] });
        }
      } catch (error) {
        if (controller.signal.aborted) return;
        clearStoredRun(tenant);
        setPending(undefined);
        setFailure({
          placement: run.placement,
          reason: 'refresh',
          detail: error instanceof Error ? error.message : undefined,
        });
      }
    },
    [],
  );

  React.useEffect(() => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setFamily(undefined);
    setPending(undefined);
    setPreview(undefined);
    setFailure(undefined);
    setCommitting(false);
    // Nothing has been read for this brand yet: the skeleton shows until a read for it applies an answer. A brand
    // still resolving (no tenant yet) holds the skeleton too.
    setReadStatus('loading');

    if (!tenantId) return () => controller.abort();
    if (!creativesServiceConfigured() && !creativeApiConfigured()) {
      // No creatives backend in this environment: there is nothing to read, so the page shows its empty state.
      setReadStatus('ready');
      return () => controller.abort();
    }

    // Resume a run this brand already had going (before the family loads, so the page comes
    // back mid-run rather than offering a fresh edit over artwork about to change).
    const stored = readStoredRun(tenantId);
    if (isCreativeRunResumable(stored, Date.now())) {
      const run = stored as StoredCreativeRun;
      setPending({ placement: run.placement, mode: run.mode, phase: 'running' });
      void follow(run, tenantId, controller);
    } else if (stored) {
      clearStoredRun(tenantId);
    }

    // Stamp the initial load with the shared family-request guard too: if a resume-a-run refetch
    // (progress/done watcher) fires while this mount fetch is still in flight, the LATER-dispatched one
    // wins — a slow initial load can never clobber a fresher refetch and strand the grid short of formats.
    // The superseded mount read then leaves the skeleton up until the later read answers.
    readFamily(tenantId, controller);

    return () => controller.abort();
  }, [tenantId, follow, readFamily]);

  // Authoritatively re-read the family until each target version holds ALL its slots (or the poll gives up).
  // The format images never arrive over the websocket — the FE rebuilds a version's `formats` from the
  // family read, and frames only trigger WHEN to re-read; a single refetch can miss the last slot (the read
  // lands a beat before it is visible, or the triggering frame was dropped), stranding the version one slot
  // short until a manual browser refresh. Each attempt applies the (guarded) result so the grid fills in as
  // slots land, and stops as soon as every target is complete.
  const confirmFormatsComplete = React.useCallback(
    async (targets: FormatTarget[], controller: AbortController): Promise<void> => {
      if (!tenantId) return;
      let remaining = targets;
      for (let attempt = 0; attempt < FORMATS_CONFIRM_ATTEMPTS && remaining.length > 0; attempt += 1) {
        const reqId = ++familyReqRef.current;
        let next: CreativeFamily | undefined;
        try {
          next = await fetchCreativeFamilyFromService(tenantId, domain, controller.signal);
        } catch {
          failRead(reqId, controller);
          return; // a failed refetch leaves the family in place; the editor's safety net still releases the bridge
        }
        if (controller.signal.aborted) return;
        applyRead(reqId, controller, next);
        remaining = formatTargetsStillIncomplete(next, remaining);
        // Complete, or out of attempts: skip the final (pointless) wait so the worst case stays well under
        // the editor's finishing-bridge safety net.
        if (remaining.length === 0 || attempt === FORMATS_CONFIRM_ATTEMPTS - 1) return;
        try {
          await sleep(FORMATS_CONFIRM_DELAY_MS, controller.signal);
        } catch {
          return; // aborted mid-wait (brand/tenant switch)
        }
      }
    },
    [tenantId, domain, applyRead, failRead],
  );

  // Each watcher below de-dupes on its own seen set, reset per brand. The jobs the list ALREADY shows for this
  // brand are reflected in the page's first read, so they are seeded as seen and only a job that finishes or
  // advances afterwards reads the family again (each such read superseded the first one, so the page sat on
  // "empty" until it answered). Seeded once the list has loaded, since on a direct load it lands after the page
  // opened; `jobsRef` keeps a job update from re-seeding.
  const seenFormatsCompleteRef = React.useRef<Set<string>>(new Set());
  const seenDoneRef = React.useRef<Set<string>>(new Set());
  const seenProgressRef = React.useRef<Set<string>>(new Set());
  React.useEffect(() => {
    const seen = seenFamilyJobs(jobsListed ? jobsRef.current : [], tenantId);
    seenFormatsCompleteRef.current = seen.formatsComplete;
    seenDoneRef.current = seen.done;
    seenProgressRef.current = seen.progress;
  }, [tenantId, jobsListed]);

  // Harden the "all formats" finish: when a formats job REACHES its total (status done, or completed has
  // caught up to total even if the done frame was dropped), poll the family until that version genuinely
  // holds every slot. This is the pull-from-backend the general watchers below don't do — they fire one
  // deduped refetch, so a stale or missed final read otherwise leaves a fully-generated version showing
  // "Generate all formats" until a manual refresh. De-duped per job id; seeded per brand (above).
  React.useEffect(() => {
    if (!tenantId || !creativesServiceConfigured()) return;
    const controller = abortRef.current;
    if (!controller) return;
    const finished = jobs.filter(
      (j) => isFormatsRunComplete(j, tenantId) && !seenFormatsCompleteRef.current.has(j.id),
    );
    if (finished.length === 0) return;
    finished.forEach((j) => seenFormatsCompleteRef.current.add(j.id));
    const targets: FormatTarget[] = [];
    finished.forEach((j) => {
      if (j.parent_id) targets.push({ versionId: j.parent_id, total: j.total ?? 0 });
    });
    void confirmFormatsComplete(targets, controller);
    // `domain` is not read here; confirmFormatsComplete (deps [tenantId, domain]) already carries it.
  }, [jobs, tenantId, confirmFormatsComplete]);

  // Refetch the family when a service job for THIS tenant finishes, so the new creative appears in
  // place (the tray shows the result the moment it lands; this brings it into the page grid too).
  // `seenDoneRef` de-dupes so each done job triggers at most one refetch; it is seeded per brand (above).
  React.useEffect(() => {
    if (!tenantId || !creativesServiceConfigured()) return;
    const controller = abortRef.current;
    if (!controller) return;
    const fresh = doneWatchJobs.filter((j) => isDoneRun(j, tenantId) && !seenDoneRef.current.has(j.id));
    if (fresh.length === 0) return;
    fresh.forEach((j) => seenDoneRef.current.add(j.id));
    const reqId = ++familyReqRef.current;
    void fetchCreativeFamilyFromService(tenantId, domain, controller.signal)
      .then((next) => applyRead(reqId, controller, next))
      // A refetch failure leaves the current family in place (the tray still shows the result); before any
      // family has shown, it is the page's error.
      .catch(() => failRead(reqId, controller));
  }, [doneWatchJobs, tenantId, domain, applyRead, failRead]);

  // Refetch the family as an active run ADVANCES (not only when it finishes), so the ALL FORMATS grid
  // fills in each slot the moment it lands — the backend persists each format as it is drawn, and the
  // job's `completed` count ticking up is our signal that a new one is ready to fetch. De-duped by
  // `${jobId}:${completed}` so a given count refetches at most once; gated on completed > 0 so a merely
  // queued job never refetches. The shared `familyReqRef` guard drops a stale (out-of-order) response.
  // Seeded per brand (above).
  React.useEffect(() => {
    if (!tenantId || !creativesServiceConfigured()) return;
    const controller = abortRef.current;
    if (!controller) return;
    const advancing = jobs
      .map((j) => progressKeyOf(j, tenantId))
      .filter((key): key is string => key !== undefined && !seenProgressRef.current.has(key));
    if (advancing.length === 0) return;
    advancing.forEach((key) => seenProgressRef.current.add(key));
    const reqId = ++familyReqRef.current;
    void fetchCreativeFamilyFromService(tenantId, domain, controller.signal)
      .then((next) => applyRead(reqId, controller, next))
      // A mid-run refetch failure just delays the grid fill (the done-watcher catches up at the end); before any
      // family has shown, it is the page's error.
      .catch(() => failRead(reqId, controller));
  }, [jobs, tenantId, domain, applyRead, failRead]);

  /** Read the family again after a failed read (the error state's "Try again"). */
  const reload = React.useCallback((): void => {
    const controller = abortRef.current;
    if (!tenantId || !controller || controller.signal.aborted) return;
    setReadStatus((status) => (status === 'failed' ? 'loading' : status));
    readFamily(tenantId, controller);
  }, [tenantId, readFamily]);

  const generate = React.useCallback(
    async (placement: string, input: GenerateInput): Promise<GenerateResult> => {
      const controller = abortRef.current;
      if (!tenantId || !controller) return { ok: false };
      if (pendingRef.current) return { ok: false }; // one run per brand at a time

      // Service path: the spark-creatives backend runs generation ASYNCHRONOUSLY. Enqueuing
      // charges credits and returns a job id immediately (no poll/preview here) — the durable
      // jobs slice + the tray track it to completion (surviving navigation / reload / re-login),
      // and the completion watcher above refetches the family when it lands. A 402 comes back as
      // ok:false so the caller can open Buy-credits. Only prompt-generation is on the service
      // today; other modes fall through to the build-ahead pipeline below.
      const prompt = (input.prompt ?? '').trim();
      // A draw from the banner's definition needs no brief (the definition says what to show).
      if (creativesServiceConfigured() && input.mode === 'generate' && (prompt || input.useDefinition)) {
        // Block a double-submit during the reference-upload + enqueue round-trip (see submittingRef).
        if (submittingRef.current) return { ok: false };
        submittingRef.current = true;
        setFailure(undefined);
        setPreview(undefined);
        try {
          // Owner-attached reference images are uploaded to CDN first (same spark-ai upload the
          // own-artwork path uses), then passed as urls so the backend reproduces them faithfully.
          let referenceImageUrls: string[] | undefined;
          if (input.referenceDataUris && input.referenceDataUris.length > 0) {
            referenceImageUrls = await Promise.all(
              input.referenceDataUris.map((uri) => uploadArtwork(uri, brandName, controller.signal)),
            );
            if (controller.signal.aborted) return { ok: false };
          }
          const result = await dispatch(
            enqueueGeneration({
              tenantId,
              placement,
              prompt,
              bannerId: input.bannerId,
              baseImageUrl: input.baseImageUrl,
              referenceImageUrls,
              parentVersionId: input.parentVersionId,
              mascotId: input.mascotId,
              useDefinition: input.useDefinition,
              styleId: input.styleId,
              domain,
            }),
          ).unwrap();
          if (controller.signal.aborted) return { ok: false };
          if (!result.ok) {
            // A 402 carries `needed` so the caller can open Buy-credits; any other error is a start
            // failure surfaced as a toast.
            setFailure({ placement, reason: 'queue' });
            return { ok: false, needed: result.needed };
          }
          return { ok: true };
        } catch (error) {
          if (controller.signal.aborted) return { ok: false };
          setFailure({ placement, reason: 'queue', detail: error instanceof Error ? error.message : undefined });
          // A refused enqueue rejects with the service's code (see `enqueueGeneration`), so the caller can say why.
          const refusal = (error as { refusal?: unknown } | null)?.refusal;
          return typeof refusal === 'string' ? { ok: false, refusal } : { ok: false };
        } finally {
          // Cleared once the enqueue settles: on success `generating` (the optimistic job) has taken
          // over as the guard; on failure the owner can retry.
          submittingRef.current = false;
        }
      }

      setFailure(undefined);
      setPreview(undefined);
      setPending({ placement, mode: input.mode, phase: input.uploadDataUri ? 'uploading' : 'running' });

      let run;
      try {
        const uploadUrl = input.uploadDataUri
          ? await uploadArtwork(input.uploadDataUri, brandName, controller.signal)
          : undefined;
        if (controller.signal.aborted) return { ok: false };
        if (input.uploadDataUri) setPending({ placement, mode: input.mode, phase: 'running' });
        run = await queueCreativeRun(
          {
            tenantId,
            placement,
            mode: input.mode,
            preview: true,
            prompt: input.prompt,
            uploadUrl,
            bannerSetId: input.bannerSetId,
            forceRegen: input.forceRegen,
          },
          controller.signal,
        );
      } catch (error) {
        if (controller.signal.aborted) return { ok: false };
        setPending(undefined);
        setFailure({ placement, reason: 'queue', detail: error instanceof Error ? error.message : undefined });
        return { ok: false };
      }

      const record: StoredCreativeRun = {
        pipelineId: run.pipelineId,
        placement,
        mode: input.mode,
        startedAt: Date.now(),
      };
      writeStoredRun(tenantId, record);
      if (controller.signal.aborted) return { ok: true };
      setPending({ placement, mode: input.mode, phase: 'running' });
      await follow(record, tenantId, controller);
      return { ok: true };
    },
    [tenantId, brandName, follow, dispatch, domain],
  );

  const apply = React.useCallback(
    async (placement: string): Promise<boolean> => {
      const controller = abortRef.current;
      if (!tenantId || !controller) return false;
      setCommitting(true);
      setFailure(undefined);
      try {
        await applyCreativePreview(tenantId, placement, controller.signal);
        const next = await fetchLiveCreativeFamily(tenantId, controller.signal);
        if (controller.signal.aborted) return false;
        setFamily(next);
        setPreview(undefined);
        return true;
      } catch (error) {
        if (controller.signal.aborted) return false;
        setFailure({ placement, reason: 'refresh', detail: error instanceof Error ? error.message : undefined });
        return false;
      } finally {
        if (!controller.signal.aborted) setCommitting(false);
      }
    },
    [tenantId],
  );

  const discard = React.useCallback((): void => {
    setPreview(undefined);
    setFailure(undefined);
  }, []);

  /**
   * Replace a logo-family creative with the owner's own artwork, through the proven
   * launched-brand pipeline: upload, queue the run, poll it, then refetch the family. `slot` is
   * the logo slot (a card's `banner_id`). Logos replace live (no preview step), so this is shown
   * as a run in `pending` rather than a brief commit spinner. Resolves true once the new artwork
   * is live and refetched; never rejects.
   */
  const placeOwn = React.useCallback(
    async (slot: string, imageDataUri: string): Promise<boolean> => {
      const controller = abortRef.current;
      if (!tenantId || !controller) return false;
      if (pendingRef.current) return false; // one run per brand at a time

      setFailure(undefined);
      setPreview(undefined);
      setPending({ placement: slot, mode: 'generate', phase: 'uploading' });

      let pipelineId: string;
      try {
        const run = await replaceLogoCreative({ tenantId, slot, imageDataUri, brandName }, controller.signal);
        pipelineId = run.pipelineId;
      } catch (error) {
        if (controller.signal.aborted) return false;
        setPending(undefined);
        setFailure({ placement: slot, reason: 'queue', detail: error instanceof Error ? error.message : undefined });
        return false;
      }

      if (controller.signal.aborted) return true;
      setPending({ placement: slot, mode: 'generate', phase: 'running' });

      const deadline = Date.now() + POLL_TIMEOUT_MS;
      try {
        while (Date.now() < deadline) {
          await sleep(POLL_INTERVAL_MS, controller.signal);
          let status;
          try {
            status = await fetchLogoCreativeRunStatus(pipelineId, tenantId, controller.signal);
          } catch (error) {
            if (controller.signal.aborted) throw error;
            continue; // a transient poll error over a minutes-long run is ordinary
          }
          if (status.failed) {
            setPending(undefined);
            setFailure({ placement: slot, reason: 'run' });
            return false;
          }
          if (status.settled) break;
        }
        const next = await fetchLiveCreativeFamily(tenantId, controller.signal);
        if (controller.signal.aborted) return true;
        setFamily(next);
        setPending(undefined);
        return true;
      } catch (error) {
        if (controller.signal.aborted) return false;
        setPending(undefined);
        setFailure({ placement: slot, reason: 'refresh', detail: error instanceof Error ? error.message : undefined });
        return false;
      }
    },
    [tenantId, brandName],
  );

  /**
   * Publish a version to the brand's live site. Enqueues the publish job (the tray + SSE surface
   * its progress, and the done-watcher refetches the family when it lands). Returns the raw result
   * so the caller can open Buy-credits on a 402. Never rejects.
   */
  const publish = React.useCallback(
    async (rootBannerId: string, versionId: string | undefined): Promise<PublishResult> => {
      const controller = abortRef.current;
      if (!tenantId || !controller || !creativesServiceConfigured()) return { ok: false };
      try {
        return await publishBanner(tenantId, rootBannerId, versionId, controller.signal);
      } catch {
        return { ok: false };
      }
    },
    [tenantId],
  );

  /**
   * Delete (archive) a regenerated version so it drops out of the family. Returns whether it was deleted.
   * The initial / live banner can't be deleted (the editor disables the action, and the backend rejects
   * it). Never rejects.
   *
   * OPTIMISTIC: flip the version into "Archived" on the local family the moment it's clicked so the editor
   * updates instantly, then confirm with the server. Previously this awaited the archive POST AND a full
   * family refetch before touching the UI, which lagged the move up to ~5s. If the server refuses (a raced
   * publish, or a call failure), the flag is flipped back so the UI re-matches the server.
   */
  const deleteVersion = React.useCallback(
    async (versionId: string): Promise<boolean> => {
      const controller = abortRef.current;
      if (!tenantId || !controller || !creativesServiceConfigured()) return false;
      setFamily((prev) => withVersionArchived(prev, versionId, true));
      // Invalidate any in-flight family refetch. A concurrent generation/publish job's progress watcher
      // replaces the whole family on every tick, ordered by this same familyReqRef guard; without the bump
      // a refetch already in flight when the owner archives resolves with a pre-archive snapshot and clobbers
      // the optimistic flip, so the version briefly flashes back as active until the next tick re-syncs.
      familyReqRef.current += 1;
      const ok = await deleteBannerVersion(tenantId, versionId, controller.signal);
      if (!ok && !controller.signal.aborted) setFamily((prev) => withVersionArchived(prev, versionId, false));
      return ok;
    },
    [tenantId],
  );

  /**
   * Recover an archived version back into the family. Returns whether it was restored. Optimistic, the
   * mirror of {@link deleteVersion}: flip it out of "Archived" instantly, reconcile only if the server refuses.
   */
  const restoreVersion = React.useCallback(
    async (versionId: string): Promise<boolean> => {
      const controller = abortRef.current;
      if (!tenantId || !controller || !creativesServiceConfigured()) return false;
      setFamily((prev) => withVersionArchived(prev, versionId, false));
      // Mirror deleteVersion: invalidate any in-flight family refetch so it cannot clobber the optimistic flip.
      familyReqRef.current += 1;
      const ok = await restoreBannerVersion(tenantId, versionId, controller.signal);
      if (!ok && !controller.signal.aborted) setFamily((prev) => withVersionArchived(prev, versionId, true));
      return ok;
    },
    [tenantId],
  );

  /**
   * Mark a banner family SEEN — clears its "new" cue the moment the owner opens the editor.
   * Optimistically drops `has_new` on the local family so the card updates instantly, then tells
   * the server (fire-and-forget; a failure just leaves the flag for the next refetch to re-sync).
   */
  const markSeen = React.useCallback(
    (rootBannerId: string): void => {
      const controller = abortRef.current;
      if (!tenantId || !controller || !creativesServiceConfigured()) return;
      setFamily((prev) => {
        if (!prev) return prev;
        let changed = false;
        const placements = prev.placements.map((p) => ({
          ...p,
          items: p.items.map((it) => {
            if (it.banner_id === rootBannerId && it.has_new) {
              changed = true;
              return { ...it, has_new: false };
            }
            return it;
          }),
        }));
        return changed ? { ...prev, placements } : prev;
      });
      void markBannerSeen(tenantId, rootBannerId, controller.signal).catch(() => {
        /* a failed mark-seen leaves the server flag; the next family refetch re-syncs it */
      });
    },
    [tenantId],
  );

  return {
    family,
    loading: readStatus === 'loading',
    loadFailed: readStatus === 'failed',
    reload,
    pending,
    preview,
    committing,
    failure,
    generate,
    apply,
    discard,
    placeOwn,
    publish,
    deleteVersion,
    restoreVersion,
    markSeen,
  };
};
